import React, { useState } from 'react';
import {
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@forgeglobal/design-framework';
import formatCurrency from '@tcs/common/utils/formatCurrency';
import { useStyles } from './style';
import InfoCard from '@tcs/common/components/molecules/InfoCard';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ClickAwayListener from '@mui/material/ClickAwayListener';

interface Props {
  title: string;
  totalValue: number;
  availableCash: number;
}

const AccountInfoCard = ({ title, totalValue, availableCash }: Props) => {
  const classes = useStyles();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  // make sure we don't display negative
  const displayAvailibleCash = Math.max(availableCash ?? 0, 0);
  const theme = useTheme();
  const medium = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <InfoCard
      title={title}
      className={classes.wrapper}
      contentClassName={classes.content}>
      <div>
        <Typography color="textSecondary" variant="body2">
          {title}
        </Typography>
        <Typography
          variant={medium ? 'h3' : 'h4'}
          color="primary"
          className={classes.amount}
          component={'div'}
          data-private>
          {formatCurrency(totalValue, true)}
        </Typography>
      </div>
      <div>
        <Typography color="textSecondary" variant="body2">
          Available Cash
          <ClickAwayListener onClickAway={() => setTooltipIsOpen(false)}>
            <Tooltip
              open={tooltipIsOpen}
              onOpen={() => setTooltipIsOpen(true)}
              onClose={() => setTooltipIsOpen(false)}
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.tooltipArrow,
              }}
              title={
                <Typography
                  variant="body2"
                  className={classes.tooltipTypography}>
                  This is the amount of cash available for investment, that
                  excludes any pending deposits, outstanding fees, and required
                  minimum cash balance.
                </Typography>
              }
              arrow>
              <InfoOutlined
                className={classes.tooltipIcon}
                onClick={() => setTooltipIsOpen(true)}
              />
            </Tooltip>
          </ClickAwayListener>
        </Typography>
        <Typography
          variant="h5"
          color="textSecondary"
          title={`${displayAvailibleCash}`}
          className={classes.availableCashAmount}
          component={'div'}
          data-private>
          {formatCurrency(displayAvailibleCash, true)}
        </Typography>
      </div>
    </InfoCard>
  );
};

export default AccountInfoCard;
